import yaml from "js-yaml";
import { ArchitectureParseError } from "../errors";
import { foldSoftWrappedQuotes } from "../parse-soft-wrap";

// ---------------------------------------------------------------------------
// v0.3 bullet-block parser
//
// The v0.3 template uses markdown bullets with YAML-style nested values
// (arrays of objects, nested objects) — far richer than the two-level
// scalar-bullet shape the v0.2 parser handles. Rather than extend that
// parser, we transform the bullet block into pure YAML and let js-yaml do
// the work.
//
// Input (markdown bullets under a bold entry):
//   - kind: payment-provider
//   - headline: "Handles all card payments and refunds."
//   - risks:
//     - category: vendor-lock-in
//       severity: warning
//       response:
//         accepted_because: "Switching cost is real..."
//
// Treated as a YAML list of single-key maps; js-yaml returns
// `[{kind:...}, {headline:...}, {risks:[...]}]`. We merge those into one
// `Record<string, unknown>` which the entity parsers then validate.
// ---------------------------------------------------------------------------

const BOLD_HEADER = /^\s*-\s+\*\*([a-zA-Z0-9_\-./]+)\*\*\s*$/;
const BOLD_HEADER_INLINE = /^\s*-\s+\*\*([a-zA-Z0-9_\-./]+)\*\*\s*[:.\-—]\s*(.+)$/;

interface BoldEntryV3 {
  id: string;
  /** Free-text label captured from a header like `- **slug**: Label`. */
  inlineLabel?: string;
  /** Pre-flatten properties record produced by parseBulletBlock. */
  properties: Record<string, unknown>;
}

/**
 * Split a section body into bold-bullet entries. Each entry is a top-level
 * `- **slug**` line followed by indented property bullets, separated from
 * sibling entries by blank lines.
 */
function splitBoldEntries(body: string): Array<{ id: string; inlineLabel?: string; body: string }> {
  const lines = body.split("\n");
  const entries: Array<{ id: string; inlineLabel?: string; body: string }> = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    const inlineMatch = line.match(BOLD_HEADER_INLINE);
    const standaloneMatch = line.match(BOLD_HEADER);
    if (!inlineMatch && !standaloneMatch) {
      i += 1;
      continue;
    }
    const id = (inlineMatch?.[1] ?? standaloneMatch?.[1] ?? "").trim();
    const inlineLabel = inlineMatch?.[2]?.trim().replace(/^"|"$/g, "");
    const buf: string[] = [];
    let j = i + 1;
    while (j < lines.length) {
      const next = lines[j] ?? "";
      if (BOLD_HEADER.test(next) || BOLD_HEADER_INLINE.test(next)) break;
      buf.push(next);
      j += 1;
    }
    entries.push({ id, inlineLabel, body: buf.join("\n") });
    i = j;
  }
  return entries;
}

/**
 * Strip the minimum leading indent across non-blank lines so the bullets
 * are anchored at column 0 before YAML parsing. Duplicates the helper in
 * `parse-sections.ts` to avoid coupling layers; both are doing the same
 * transformation at different scopes.
 */
function stripCommonIndent(body: string): string {
  const lines = body.split("\n");
  let min = Number.POSITIVE_INFINITY;
  for (const line of lines) {
    if (line.trim().length === 0) continue;
    const m = line.match(/^[ \t]*/);
    const indent = m ? m[0].length : 0;
    if (indent < min) min = indent;
    if (min === 0) break;
  }
  if (!Number.isFinite(min) || min === 0) return body;
  return lines.map((line) => (line.trim().length === 0 ? line : line.slice(min))).join("\n");
}

/**
 * Parse a markdown bullet block into a flat properties record. The block
 * is the body of a bold entry — everything after the `- **slug**` line and
 * before the next bold entry.
 *
 * Throws ArchitectureParseError if YAML parsing fails so the importer
 * surfaces a structured error rather than rendering with mangled data.
 */
function parseBulletBlock(body: string, context: string): Record<string, unknown> {
  const folded = foldSoftWrappedQuotes(body);
  const dedented = stripCommonIndent(folded);
  if (dedented.trim().length === 0) return {};

  let raw: unknown;
  try {
    raw = yaml.load(dedented);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new ArchitectureParseError(`Failed to parse bullet block under \`${context}\`: ${msg}`);
  }

  if (raw === null || raw === undefined) return {};
  if (Array.isArray(raw)) {
    const merged: Record<string, unknown> = {};
    for (const entry of raw) {
      if (entry && typeof entry === "object" && !Array.isArray(entry)) {
        Object.assign(merged, entry as Record<string, unknown>);
      }
    }
    return merged;
  }
  if (typeof raw === "object") {
    return raw as Record<string, unknown>;
  }
  return {};
}

/**
 * Combined helper: split a section body into bold entries and parse each
 * one's bullet block into properties. Convenience for entity parsers that
 * always do both.
 */
export function parseBoldEntriesV3(body: string, context: string): BoldEntryV3[] {
  return splitBoldEntries(body).map(({ id, inlineLabel, body: entryBody }) => ({
    id,
    inlineLabel,
    properties: parseBulletBlock(entryBody, `${context} → ${id}`),
  }));
}

// === Field coercion helpers ===============================================

export function asString(value: unknown): string | undefined {
  if (typeof value === "string") return value.trim() === "" ? undefined : value;
  return undefined;
}

export function asStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .filter((v): v is string => typeof v === "string" && v.trim().length > 0)
      .map((v) => v.trim());
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return [value.trim()];
  }
  return [];
}

export function asBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const lower = value.trim().toLowerCase();
    if (lower === "true" || lower === "yes") return true;
    if (lower === "false" || lower === "no") return false;
  }
  return undefined;
}
